import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowRight, Heart, Users, GraduationCap, Globe, Award,
  BookOpen, Sparkles, HandHeart, Target, BarChart3, School,
  CheckCircle2, Star, Gift, Calendar
} from 'lucide-react';
import { useBotShield } from '../../hooks/useBotShield';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CommunityImpactPage() {
  const [donateForm, setDonateForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const { HoneypotField, getPayloadFields } = useBotShield();

  const handleVolunteerInquiry = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/contact/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: donateForm.name,
          email: donateForm.email,
          subject: 'Community Impact — volunteer/donor inquiry',
          message: donateForm.message,
          source: 'volunteer',
          ...getPayloadFields(),
        }),
      });
    } catch {}
    toast.success('Thank you for your interest! We will contact you soon.');
    setDonateForm({ name: '', email: '', message: '' });
    setSubmitting(false);
  };

  const impactStats = [
    { value: '93%', label: 'Students from families below Area Median Income', icon: Heart },
    { value: '12,000+', label: 'Learners served through free programs', icon: Users },
    { value: '49%', label: 'Female or non-binary learners', icon: Sparkles },
    { value: '6', label: 'Diverse ethnic communities reached', icon: Globe },
  ];

  const demographics = [
    { label: 'Latino/Hispanic', value: 43, color: 'bg-orange-500' },
    { label: 'Multi-racial', value: 25, color: 'bg-blue-500' },
    { label: 'Black/African American', value: 12, color: 'bg-purple-500' },
    { label: 'Asian', value: 10, color: 'bg-green-500' },
    { label: 'White', value: 10, color: 'bg-cyan-500' },
  ];

  const programs = [
    { title: 'Free Coding Classes', description: 'Teacher-led, in-person coding courses for grades 1-8 at no cost to qualifying families.', icon: BookOpen },
    { title: 'STEM Camps & Workshops', description: 'Seasonal camps covering robotics, game design, web development, and more.', icon: Calendar },
    { title: 'Scholarship Programs', description: 'Full scholarships for Ready Set STEM paid courses for qualifying students.', icon: Award },
    { title: 'School Partnerships', description: 'Bringing coding education directly into underserved schools and community centers.', icon: School },
  ];

  const timeline = [
    { year: 'Founded', title: 'APS Training Academy Established', description: 'Founded by Harish Ananthapadmanabhan in Aurora, IL to honor his mother Leela Padmanabhan and her legacy of community service.' },
    { year: 'Growth', title: 'Ready Set STEM Partnership', description: 'APS Training Academy and Ready Set STEM joined forces to combine free community programs with a world-class STEM curriculum.' },
    { year: 'Impact', title: 'Serving 12,000+ Learners', description: 'Together, we have served over 12,000 learners across multiple communities, with 93% from underserved families.' },
    { year: 'Future', title: 'Expanding Our Reach', description: 'Our vision: make quality STEM education accessible to every child, regardless of their background or family income.' },
  ];

  return (
    <div data-testid="community-impact-page">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1697643635403-116830fe9d39?w=1920')] bg-cover bg-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-rsc-navy/95 via-rsc-navy/80 to-rsc-navy/60" />
        <div className="container-app relative z-10 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rsc-yellow/20 text-rsc-yellow text-sm font-semibold mb-6">
              <HandHeart className="w-4 h-4" /> Powered by APS Training Academy
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
              Giving Back Through<br />
              <span className="text-rsc-yellow">STEM Education</span>
            </h1>
            <p className="text-xl text-white/90 mb-4">
              Ready Set STEM and APS Training Academy are united in a shared mission: ensuring every child has access to quality coding and STEM education, regardless of their family's income.
            </p>
            <p className="text-lg text-white/70 mb-8">
              APS Training Academy is a 501(c)(3) non-profit providing free STEAM education to underserved communities in Aurora, IL and beyond.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/catalog">
                <Button className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright text-lg px-8 py-6 font-bold rounded-full">
                  Explore Free Programs <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#get-involved">
                <Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">
                  Get Involved
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-12 bg-white border-b">
        <div className="container-app">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {impactStats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={i} className="text-center" data-testid={`impact-stat-${i}`}>
                  <div className="w-12 h-12 rounded-full bg-rsc-blue/10 flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-6 h-6 text-rsc-blue" />
                  </div>
                  <p className="text-3xl md:text-4xl font-black text-rsc-navy">{stat.value}</p>
                  <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-rsc-blue font-semibold text-sm mb-4">
                <Heart className="w-4 h-4" /> OUR STORY
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-6">
                Two Organizations,<br />One Shared Mission
              </h2>
              <p className="text-gray-700 text-lg mb-4">
                <strong>APS Training Academy</strong> was founded by Harish Ananthapadmanabhan in Aurora, Illinois, 
                to honor his mother, <strong>Leela Padmanabhan</strong>, and continue her lifelong legacy of community service 
                and empowering others through education.
              </p>
              <p className="text-gray-700 text-lg mb-4">
                As a registered 501(c)(3) non-profit, APS Training Academy provides <strong>free and low-cost 
                STEAM education</strong> to underserved communities — ensuring that a family's zip code or income 
                never determines a child's access to technology education.
              </p>
              <p className="text-gray-700 text-lg mb-6">
                <strong>Ready Set STEM</strong> is the curriculum and technology platform that powers APS Training Academy's 
                programs, combining STEM-accredited coursework with gamification, progress tracking, and a community of 
                passionate instructors — all designed to make coding fun and accessible for every child.
              </p>
              <div className="flex items-center gap-4 p-4 bg-rsc-yellow/10 rounded-xl border border-rsc-yellow/20">
                <div className="w-12 h-12 rounded-full bg-rsc-yellow flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-rsc-navy" />
                </div>
                <div>
                  <p className="font-semibold text-rsc-navy">Best In STEM™ + Accredited™ Trustmark</p>
                  <p className="text-sm text-gray-600">Dual STEM.org recognition — Top 5% of programs worldwide for quality, rigor, and integrity.</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1653566031471-85365f8d0ae0?w=800" 
                  alt="Diverse students learning technology together"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-xs border-l-4 border-rsc-blue">
                <p className="font-bold text-rsc-navy text-lg">93% of our students</p>
                <p className="text-gray-600 text-sm">come from families below the Area Median Income</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-4">Our Journey Together</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">From a single classroom to serving hundreds of students across multiple communities.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            {timeline.map((item, i) => (
              <div key={i} className="flex gap-6 mb-8 last:mb-0">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-rsc-navy flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                    {item.year}
                  </div>
                  {i < timeline.length - 1 && <div className="w-0.5 flex-1 bg-rsc-navy/20 mt-2" />}
                </div>
                <div className="pb-8">
                  <h3 className="font-heading text-xl font-bold text-rsc-navy mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demographics Section */}
      <section className="section-padding bg-rsc-navy text-white">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Serving Our Diverse Community
              </h2>
              <p className="text-white/80 text-lg mb-8">
                We're proud to serve one of the most diverse student populations in STEM education. 
                Our programs intentionally reach communities that have been historically underrepresented in technology.
              </p>
              <div className="space-y-4">
                {demographics.map((demo, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-24 text-right text-sm text-white/70">{demo.label}</div>
                    <div className="flex-1 h-6 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${demo.color} rounded-full flex items-center justify-end pr-2 transition-all duration-1000`}
                        style={{ width: `${demo.value}%` }}
                      >
                        <span className="text-xs font-bold text-white">{demo.value}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-white/60 text-sm mt-6 italic">
                * 49% of our learners identify as female or non-binary
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: GraduationCap, label: 'Grades 1-8', desc: 'Age-appropriate curriculum' },
                { icon: HandHeart, label: 'Free Programs', desc: 'For qualifying families' },
                { icon: Globe, label: 'Aurora, IL', desc: 'And expanding nationally' },
                { icon: Target, label: 'Economic Equality', desc: 'Through STEM workforce' },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <Card key={i} className="bg-white/5 border-white/10">
                    <CardContent className="p-6">
                      <Icon className="w-8 h-8 text-rsc-yellow mb-3" />
                      <p className="font-bold text-white">{item.label}</p>
                      <p className="text-sm text-white/60">{item.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="section-padding">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-4">Free & Subsidized Programs</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">Through APS Training Academy, we offer multiple pathways for underserved families to access quality STEM education.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {programs.map((prog, i) => {
              const Icon = prog.icon;
              return (
                <Card key={i} className="card-marketing h-full" data-testid={`community-program-${i}`}>
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-rsc-blue/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-rsc-blue" />
                    </div>
                    <h3 className="font-heading text-lg font-bold text-rsc-navy mb-2">{prog.title}</h3>
                    <p className="text-gray-600 text-sm">{prog.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How to Access Free Programs */}
      <section className="section-padding bg-gradient-to-br from-rsc-blue/5 to-rsc-yellow/5">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-4">How to Access Free Programs</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">Getting started is easy. Follow these steps to apply for free coding classes through APS Training Academy.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { step: '1', title: 'Check Eligibility', desc: 'Families below the Area Median Income qualify for free programs.' },
              { step: '2', title: 'Submit Application', desc: 'Complete the application form with your family information.' },
              { step: '3', title: 'Get Approved', desc: 'Our team reviews applications and notifies families within 5-7 days.' },
              { step: '4', title: 'Start Learning!', desc: 'Your child joins teacher-led, in-person coding classes at no cost.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-14 h-14 rounded-full bg-rsc-navy text-white text-xl font-bold flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-heading text-lg font-bold text-rsc-navy mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/contact">
              <Button className="btn-primary text-lg px-8 py-6">
                Apply for Free Programs <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Get Involved Section */}
      <section id="get-involved" className="section-padding bg-white">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-6">Get Involved</h2>
              <p className="text-gray-700 text-lg mb-8">
                Whether you want to volunteer, donate, or partner with us — there are many ways to support our mission of making STEM education accessible to all children.
              </p>
              <div className="space-y-6">
                {[
                  { icon: HandHeart, title: 'Volunteer as an Instructor', desc: 'Share your coding skills and inspire the next generation. No teaching experience required — we provide training.' },
                  { icon: Gift, title: 'Donate STEM Kits', desc: 'Help us equip classrooms with laptops, robots, and learning materials for hands-on education.' },
                  { icon: School, title: 'School Partnerships', desc: 'Bring free coding programs to your school or community center through our partnership program.' },
                  { icon: Star, title: 'Corporate Sponsorship', desc: 'Align your brand with educational equity. Sponsor a classroom, event, or scholarship fund.' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-rsc-yellow/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-rsc-navy" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-rsc-navy mb-1">{item.title}</h3>
                        <p className="text-gray-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <Card className="card-dashboard">
              <CardContent className="p-8">
                <h3 className="font-heading text-2xl font-bold text-rsc-navy mb-6">
                  Interested in Supporting Our Mission?
                </h3>
                <form onSubmit={handleVolunteerInquiry} className="space-y-4">
                  <HoneypotField />
                  <div>
                    <Label htmlFor="vol-name">Your Name</Label>
                    <Input 
                      id="vol-name" 
                      value={donateForm.name} 
                      onChange={(e) => setDonateForm({ ...donateForm, name: e.target.value })} 
                      required 
                    />
                  </div>
                  <div>
                    <Label htmlFor="vol-email">Email</Label>
                    <Input 
                      id="vol-email" 
                      type="email" 
                      value={donateForm.email} 
                      onChange={(e) => setDonateForm({ ...donateForm, email: e.target.value })} 
                      required 
                    />
                  </div>
                  <div>
                    <Label htmlFor="vol-message">How would you like to help?</Label>
                    <textarea 
                      id="vol-message" 
                      value={donateForm.message} 
                      onChange={(e) => setDonateForm({ ...donateForm, message: e.target.value })} 
                      className="w-full min-h-[100px] p-3 border rounded-md resize-none" 
                      placeholder="I'd like to volunteer, donate, partner..."
                      required 
                    />
                  </div>
                  <Button type="submit" className="btn-primary w-full" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Submit Inquiry'}
                  </Button>
                </form>
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>APS Training Academy</strong> is a registered 501(c)(3) non-profit organization.
                    <br />EIN: 83-4284332
                    <br />105 E. Galena Blvd, Suite 400, Aurora, IL 60505
                    <br />Phone: (331) 201-2687
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-gradient-to-r from-rsc-blue to-rsc-navy text-white">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Every Child Deserves a Chance to Code
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Together, Ready Set STEM and APS Training Academy are building a future where technology education 
            is a right, not a privilege. Join us in making a difference.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/catalog">
              <Button className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright text-lg px-8 py-6 font-bold rounded-full">
                Browse All Courses <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/apply">
              <Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">
                Join Our Team
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
